import { Controller } from "@hotwired/stimulus"

// Drives the right-sidebar status log on the listing editor's Walkthrough tab.
// Shows a live step-by-step feed while clips generate, then reveals the combined video.
export default class extends Controller {
  static values  = { pollUrl: String }
  static targets = ["scene", "combineRow", "videoWrap", "videoEl", "addBtn", "failedMsg"]

  connect() {
    this._seen   = 0
    this._active = -1
    this._lastPoll = null
    if (this.hasPollUrlValue) this._start()
  }

  disconnect() { this._stop() }

  _start() {
    this._timer = setInterval(() => this._poll(), 4000)
    this._poll()   // immediate first check
  }

  _stop() { clearInterval(this._timer) }

  async _poll() {
    try {
      const r = await fetch(this.pollUrlValue, { headers: { Accept: "application/json" } })
      if (!r.ok) return
      const d = await r.json()
      this._apply(d)
    } catch (_) { /* network glitch — keep polling */ }
  }

  _apply({ status, combine_status, clips_completed, clips_total, combined_video_url }) {
    const completed = clips_completed ?? 0
    const total     = clips_total ?? 0
    const panelOnly = this.sceneTargets.length === 0

    if (panelOnly) {
      const prev = this._lastPoll
      if (prev) {
        if (combine_status === "completed" && combined_video_url) {
          this._stop()
          this._reloadPage()
          return
        }
        if (status === "failed" || combine_status === "failed") {
          this._stop()
          this._showFailed()
          return
        }
        const clipsAdvanced = completed > (prev.clips_completed ?? 0)
        const stitchingStarted = status === "completed" && prev.status !== "completed"
        const combining = combine_status === "processing" && prev.combine_status !== "processing"
        if (clipsAdvanced || stitchingStarted || combining) {
          this._reloadPage()
          return
        }
      }
      this._lastPoll = { clips_completed: completed, status, combine_status }
      return
    }

    // Mark newly finished scenes (sidebar step log)
    while (this._seen < completed) {
      this._markDone(this._seen)
      this._seen++
    }

    // Pulse the currently-generating scene
    if (status === "processing" && completed < total) {
      this._pulse(completed)
    } else if (status === "completed" || status === "failed") {
      this._clearPulse()
    }

    // Combine row
    if (combine_status === "pending" || combine_status === "processing") {
      this._activateCombine()
    }

    if (combine_status === "completed" && combined_video_url) {
      this._stop()
      this._reloadPage()
      return
    }

    if (status === "failed" || combine_status === "failed") {
      this._stop()
      this._showFailed()
    }
  }

  _reloadPage() {
    window.Turbo?.visit(window.location.href, { action: "replace" }) ?? window.location.reload()
  }

  _markDone(idx) {
    const el = this.sceneTargets[idx]
    if (!el) return
    el.dataset.state = "done"
  }

  _pulse(idx) {
    // Clear previous active
    if (this._active !== idx) {
      if (this._active >= 0) {
        const prev = this.sceneTargets[this._active]
        if (prev && prev.dataset.state === "active") prev.dataset.state = "pending"
      }
      const el = this.sceneTargets[idx]
      if (el && el.dataset.state !== "done") el.dataset.state = "active"
      this._active = idx
    }
  }

  _clearPulse() {
    if (this._active >= 0) {
      const el = this.sceneTargets[this._active]
      if (el && el.dataset.state === "active") el.dataset.state = "pending"
      this._active = -1
    }
  }

  _activateCombine() {
    if (this.hasCombineRowTarget) this.combineRowTarget.dataset.state = "active"
  }

  _showVideo(url) {
    if (this.hasCombineRowTarget) this.combineRowTarget.dataset.state = "done"
    if (this.hasVideoWrapTarget) {
      this.videoWrapTarget.hidden = false
      if (this.hasVideoElTarget) {
        const video = this.videoElTarget
        const busted = url.includes("?") ? `${url}&_=${Date.now()}` : `${url}?_=${Date.now()}`
        let source = video.querySelector("source")
        if (!source) {
          source = document.createElement("source")
          video.appendChild(source)
        }
        source.src = busted
        source.type = "video/mp4"
        video.load()
      }
      if (this.hasAddBtnTarget) this.addBtnTarget.hidden = false
    }
  }

  _showFailed() {
    if (this.hasFailedMsgTarget) this.failedMsgTarget.hidden = false
  }
}
